<!-- Backdrop -->
<div class="im-backdrop" (click)="close()" role="dialog" aria-modal="true" aria-label="Import Excel data"></div>

<!-- Modal card -->
<div class="im-card">

  <!-- Header -->
  <div class="im-header">
    <div class="im-header-left">
      <i class="fa-solid fa-file-import"></i>
      <h2 class="im-title">Import Excel Data</h2>
    </div>
    <button (click)="close()" class="im-close-btn" aria-label="Close">
      <i class="fa-solid fa-xmark"></i>
    </button>
  </div>

  <!-- ══════════ STATE: IDLE ══════════ -->
  @if (state() === 'idle') {
    <div class="im-body">

      <!-- Drop zone -->
      <div class="im-dropzone"
           [class.im-dropzone--active]="isDragging()"
           (dragover)="onDragOver($event)"
           (dragleave)="onDragLeave()"
           (drop)="onDrop($event)">
        <i class="fa-solid fa-cloud-arrow-up im-drop-icon"></i>
        <p class="im-drop-text">Drag & drop your Excel file here</p>
        <p class="im-drop-sub">or</p>
        <label class="im-file-label" for="excelFileInput">
          Browse File
          <input id="excelFileInput" type="file" accept=".xlsx,.xls"
                 (change)="onFileInputChange($event)" class="im-file-input"
                 aria-label="Select Excel file" />
        </label>
        <p class="im-drop-hint">.xlsx or .xls files only</p>
      </div>

      @if (fileError()) {
        <p class="im-error" role="alert">
          <i class="fa-solid fa-triangle-exclamation"></i> {{ fileError() }}
        </p>
      }

      <!-- Format guide -->
      <div class="im-guide">
        <p class="im-guide-title">Expected Excel formats:</p>
        <div class="im-guide-grid">
          <div class="im-guide-card">
            <span class="im-guide-badge">Abstract</span>
            <p>Each sheet = one Block. Columns: <em>GP Name, No. of Projects, Total Cost</em></p>
          </div>
          <div class="im-guide-card">
            <span class="im-guide-badge im-guide-badge--blue">Detailed</span>
            <p>Each sheet = one Block. Columns: <em>GP Name, Project Name, Scheme, Cost, Status, Executor</em></p>
          </div>
        </div>
      </div>

    </div>
  }

  <!-- ══════════ STATE: PREVIEW ══════════ -->
  @if (state() === 'preview') {
    <div class="im-body">

      <div class="im-preview-summary">
        <i class="fa-solid fa-file-excel" style="color:#16a34a"></i>
        <span>
          Parsed <strong>{{ sheets().length }}</strong> sheet(s) from Excel.
          @if (importPlan()) {
            <strong>{{ importPlan()!.totalRowsFound }}</strong> project row(s) found.
          }
        </span>
      </div>

      @if (parseErrors().length > 0) {
        <div class="im-warn-box" role="alert">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <div>
            <strong>{{ parseErrors().length }} parse warning(s):</strong>
            @for (e of parseErrors(); track e) {
              <p class="im-warn-item">{{ e }}</p>
            }
          </div>
        </div>
      }

      <!-- Block selector — shown only for GP-per-sheet files (each tab = one GP) -->
      @if (hasGpPerSheetFormat()) {
        <div class="im-block-select-wrap">
          <label class="im-block-select-label" for="gpBlockSelect">
            <i class="fa-solid fa-map-location-dot"></i>
            Which block do these GPs belong to?
          </label>
          <select id="gpBlockSelect"
                  [ngModel]="gpBlockName()" (ngModelChange)="onBlockChange($event)"
                  class="im-block-select"
                  aria-label="Select block for imported GPs">
            <option value="">— Select Block —</option>
            @for (b of fieldExecutionBlocks; track b) {
              <option [value]="b">{{ b }}</option>
            }
          </select>
          @if (!gpBlockName()) {
            <p class="im-block-required" role="alert">
              <i class="fa-solid fa-circle-info"></i> Select a block to enable import.
            </p>
          }
        </div>
      }

      <!-- GP list (compact) — all gp-per-sheet format -->
      @if (hasGpPerSheetFormat()) {
        <div class="im-gp-list">
          <div class="im-gp-list-header">
            <span>GP / Village</span>
            <span>Projects</span>
          </div>
          @for (sheet of sheets(); track sheet.sheetName; let odd = $odd) {
            <div class="im-gp-row" [class.im-gp-row--odd]="odd" [class.im-gp-row--invalid]="isGpSheetInvalid(sheet)">
              <div class="im-gp-row-left">
                <i class="fa-solid fa-house-chimney im-gp-icon"></i>
                <div class="im-gp-row-info">
                  <span class="im-gp-name">{{ sheet.gpName }}</span>
                  @if (sheet.villages && sheet.villages.length > 0) {
                    <div class="im-village-chips">
                      @for (v of sheet.villages.slice(0, 6); track v) {
                        <span class="im-village-chip">{{ v }}</span>
                      }
                      @if (sheet.villages.length > 6) {
                        <span class="im-village-chip im-village-chip--more">+{{ sheet.villages.length - 6 }} more</span>
                      }
                    </div>
                  }
                </div>
              </div>
              <span class="im-gp-count">{{ sheet.totalRows }}</span>
            </div>
          }
          <div class="im-gp-list-footer">
            <span>Total</span>
            <span class="im-gp-total">{{ totalRowsFound() }}</span>
          </div>
        </div>
      }

      <!-- Sheet cards — abstract / detailed format -->
      @if (!hasGpPerSheetFormat()) {
        @for (sheet of sheets(); track sheet.sheetName) {
          <div class="im-sheet-card">
            <div class="im-sheet-header">
              <div>
                <span class="im-sheet-name">{{ sheet.sheetName }}</span>
                <span class="im-sheet-block">→ Block: <strong>{{ sheet.blockName }}</strong></span>
              </div>
              <div class="im-sheet-meta">
                <span class="im-badge" [class.im-badge--blue]="sheet.format !== 'abstract'">
                  {{ sheet.format === 'abstract' ? 'GP Abstract' : 'Detailed' }}
                </span>
                <span class="im-badge im-badge--gray">{{ sheet.totalRows }} rows</span>
              </div>
            </div>

            <!-- Preview table — abstract -->
            @if (sheet.format === 'abstract') {
              <table class="im-table">
                <thead>
                  <tr>
                    <th>GP / Village</th>
                    <th class="im-td-right">Projects</th>
                    <th class="im-td-right">Est. Cost (&#8377;L)</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of previewGpRows(sheet, 5); track row.gpName) {
                    <tr>
                      <td>{{ row.gpName }}</td>
                      <td class="im-td-right">{{ row.projectCount }}</td>
                      <td class="im-td-right">{{ row.totalCostLakh }}</td>
                    </tr>
                  }
                </tbody>
              </table>
              @if (sheet.totalRows > 5) {
                <p class="im-table-more">… and {{ sheet.totalRows - 5 }} more GP(s)</p>
              }
            }

            <!-- Preview table — detailed -->
            @if (sheet.format === 'detailed') {
              <table class="im-table">
                <thead>
                  <tr>
                    <th>GP / Village</th>
                    <th>Project Name</th>
                    <th class="im-td-right">Cost (&#8377;L)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of previewProjectRows(sheet, 5); track row.projectName) {
                    <tr>
                      <td>{{ row.gpName }}</td>
                      <td>{{ row.projectName }}</td>
                      <td class="im-td-right">{{ row.estCostLakh }}</td>
                      <td>{{ row.status }}</td>
                    </tr>
                  }
                </tbody>
              </table>
              @if (sheet.totalRows > 5) {
                <p class="im-table-more">… and {{ sheet.totalRows - 5 }} more project(s)</p>
              }
            }
          </div>
        }
      }

      <!-- Step 1–2: Block + GP master validation -->
      @if ((hasGpPerSheetFormat() && gpBlockName()) || !hasGpPerSheetFormat()) {
        <div class="im-step-box"
             [class.im-step-box--ok]="importPlan()?.gpValidationPassed"
             [class.im-step-box--error]="importPlan() && !importPlan()!.gpValidationPassed">
          <div class="im-step-head">
            <span class="im-step-num">1–2</span>
            <div>
              <p class="im-step-title">Block &amp; GP validation</p>
              <p class="im-step-sub">Every sheet GP must match the master list for the selected block. Import is blocked until all GP issues are resolved.</p>
            </div>
          </div>

          @if (validationLoading()) {
            <p class="im-step-status"><i class="fa-solid fa-spinner fa-spin"></i> Checking GPs against master data…</p>
          } @else if (planError()) {
            <p class="im-step-status im-step-status--error" role="alert">
              <i class="fa-solid fa-triangle-exclamation"></i> {{ planError() }}
            </p>
          } @else if (!importPlan() && !gpBlockName()) {
            <p class="im-step-status im-step-status--muted">Select a block to validate GP names.</p>
          } @else if (!importPlan()) {
            <p class="im-step-status im-step-status--muted">
              <i class="fa-solid fa-spinner fa-spin"></i> Preparing import preview…
            </p>
          } @else if (importPlan()!.gpValidationPassed) {
            <p class="im-step-status im-step-status--ok">
              <i class="fa-solid fa-circle-check"></i>
              All {{ sheets().length }} GP(s) are valid for <strong>{{ gpBlockName() || sheets()[0]?.blockName }}</strong>.
            </p>
          } @else {
            <ul class="im-validation-errors">
              @for (e of importPlan()!.validationErrors; track e) {
                <li>{{ e }}</li>
              }
            </ul>
          }
        </div>
      }

      <!-- Import review summary -->
      @if (importPlan()?.gpValidationPassed) {
        <div class="im-review-box">
          <p class="im-review-title"><i class="fa-solid fa-clipboard-check"></i> Import review</p>

          <div class="im-review-grid">
            <div class="im-review-row">
              <span class="im-review-lbl">Total rows found</span>
              <span class="im-review-val">{{ importPlan()!.totalRowsFound }}</span>
            </div>
            <div class="im-review-row">
              <span class="im-review-lbl">Valid new projects</span>
              <span class="im-review-val im-review-val--green">{{ importPlan()!.validNewCount }}</span>
            </div>
            <div class="im-review-row">
              <span class="im-review-lbl">Duplicate projects</span>
              <span class="im-review-val im-review-val--amber">{{ importPlan()!.duplicateCount }}</span>
            </div>
            <div class="im-review-row">
              <span class="im-review-lbl">Invalid projects</span>
              <span class="im-review-val im-review-val--red">{{ importPlan()!.invalidCount }}</span>
            </div>
          </div>

          @if (importPlan()!.duplicateSamples.length) {
            <details class="im-dup-details">
              <summary>View duplicate examples ({{ importPlan()!.duplicateCount }})</summary>
              <ul class="im-dup-samples">
                @for (s of importPlan()!.duplicateSamples; track s) {
                  <li>{{ s }}</li>
                }
              </ul>
            </details>
          }

          <div class="im-review-action">
            <label class="im-action-label" for="dupActionSelect">When duplicates are found</label>
            <select id="dupActionSelect" class="im-action-select"
                    [ngModel]="duplicateHandling()"
                    (ngModelChange)="onDuplicateActionChange($event)"
                    [disabled]="validationLoading()">
              @for (opt of duplicateActionOptions; track opt.value) {
                <option [value]="opt.value">{{ opt.label }}</option>
              }
            </select>
            <p class="im-action-note">
              <strong>Update existing</strong> — matching rows overwrite cost, project name (e.g. X → X_2),
              and other fields; non-matching rows are added as new projects.<br>
              <strong>Skip duplicates</strong> — existing rows are ignored; only new rows are imported.<br>
              <strong>Allow duplicates</strong> — every Excel row is imported as a new record (no duplicate check).<br>
              Match uses Block + GP + Village (+ scheme for renames). Never creates GPs, Blocks, NACs, or Departments.
            </p>
          </div>

          <div class="im-review-final">
            <p class="im-review-final-title">Final result</p>
            <div class="im-review-grid">
              <div class="im-review-row">
                <span class="im-review-lbl">Selected action</span>
                <span class="im-review-val">{{ importPlan()!.selectedActionLabel }}</span>
              </div>
              <div class="im-review-row">
                <span class="im-review-lbl">Projects to create</span>
                <span class="im-review-val im-review-val--green">{{ importPlan()!.toCreate }}</span>
              </div>
              <div class="im-review-row">
                <span class="im-review-lbl">Projects to update</span>
                <span class="im-review-val im-review-val--blue">{{ importPlan()!.toUpdate }}</span>
              </div>
              @if (importPlan()!.toSkip > 0) {
                <div class="im-review-row">
                  <span class="im-review-lbl">Projects to skip</span>
                  <span class="im-review-val im-review-val--amber">{{ importPlan()!.toSkip }}</span>
                </div>
              }
            </div>
          </div>

          @if (importPlan()!.statusMessage) {
            @if (!importPlan()!.canImport) {
              <p class="im-status-alert" role="alert">
                <i class="fa-solid fa-circle-info"></i> {{ importPlan()!.statusMessage }}
              </p>
            } @else {
              <p class="im-status-info">
                <i class="fa-solid fa-circle-check"></i> {{ importPlan()!.statusMessage }}
              </p>
            }
          }
        </div>
      }

    </div>

    <!-- Footer -->
    @if (importPlan()?.statusMessage && !canStartImport()) {
      <p class="im-footer-alert" role="alert">
        <i class="fa-solid fa-circle-info"></i> {{ importPlan()!.statusMessage }}
      </p>
    }
    <div class="im-footer">
      <button (click)="resetToIdle()" class="im-btn im-btn--ghost">
        <i class="fa-solid fa-arrow-left"></i> Back
      </button>
      <button (click)="startImport()" class="im-btn im-btn--primary"
              [disabled]="!canStartImport()">
        <i class="fa-solid fa-file-import"></i>
        {{ importPlan()?.buttonLabel || 'Import' }}
      </button>
    </div>
  }

  <!-- ══════════ STATE: IMPORTING ══════════ -->
  @if (state() === 'importing') {
    <div class="im-body im-body--center">
      <i class="fa-solid fa-spinner fa-spin im-spinner"></i>
      <p class="im-progress-label">
        Processing {{ progressDone() }} / {{ progressTotal() }} project(s)…
      </p>
      <div class="im-progress-track" role="progressbar"
           [attr.aria-valuenow]="progressPct()" aria-valuemin="0" aria-valuemax="100">
        <div class="im-progress-fill" [style.width.%]="progressPct()"></div>
      </div>
      <p class="im-progress-pct">{{ progressPct() }}%</p>
    </div>
  }

  <!-- ══════════ STATE: DONE ══════════ -->
  @if (state() === 'done' && importResult()) {
    <div class="im-body im-body--center">

      @if (importResult()!.validationErrors.length > 0) {
        <i class="fa-solid fa-circle-xmark im-result-icon im-result-icon--error"></i>
        <h3 class="im-result-title">Import blocked</h3>
      } @else if (importResult()!.rejected === 0 && importResult()!.errors.length === 0) {
        <i class="fa-solid fa-circle-check im-result-icon im-result-icon--success"></i>
        <h3 class="im-result-title">Import complete</h3>
      } @else {
        <i class="fa-solid fa-triangle-exclamation im-result-icon im-result-icon--warn"></i>
        <h3 class="im-result-title">Import finished with issues</h3>
      }

      <div class="im-result-stats">
        <div class="im-result-stat">
          <span class="im-result-val im-result-val--green">{{ importResult()!.imported }}</span>
          <span class="im-result-lbl">Projects Created</span>
        </div>
        <div class="im-result-stat">
          <span class="im-result-val im-result-val--blue">{{ importResult()!.updated }}</span>
          <span class="im-result-lbl">Projects Updated</span>
        </div>
        <div class="im-result-stat">
          <span class="im-result-val im-result-val--amber">{{ importResult()!.skipped }}</span>
          <span class="im-result-lbl">Projects Skipped</span>
        </div>
        <div class="im-result-stat">
          <span class="im-result-val im-result-val--red">{{ importResult()!.rejected }}</span>
          <span class="im-result-lbl">Projects Rejected</span>
        </div>
      </div>

      @if (importResult()!.validationErrors.length > 0) {
        <details class="im-detail-box im-detail-box--error" open>
          <summary class="im-detail-summary">
            <i class="fa-solid fa-shield-halved"></i>
            {{ importResult()!.validationErrors.length }} Validation Error(s)
          </summary>
          <ul class="im-detail-list">
            @for (e of importResult()!.validationErrors; track e) {
              <li>{{ e }}</li>
            }
          </ul>
        </details>
      }

      @if (importResult()!.errors.length > 0) {
        <details class="im-detail-box im-detail-box--error">
          <summary class="im-detail-summary">
            <i class="fa-solid fa-triangle-exclamation"></i>
            {{ importResult()!.errors.length }} processing error(s)
          </summary>
          <ul class="im-detail-list">
            @for (e of importResult()!.errors; track e) {
              <li>{{ e }}</li>
            }
          </ul>
        </details>
      }

      @if (importResult()!.skippedItems.length > 0) {
        <details class="im-detail-box im-detail-box--skip">
          <summary class="im-detail-summary">
            <i class="fa-solid fa-forward"></i>
            {{ importResult()!.skippedItems.length }} skipped (already exist) — click to expand
          </summary>
          <ul class="im-detail-list">
            @for (s of importResult()!.skippedItems; track s) {
              <li>{{ s }}</li>
            }
          </ul>
          <div class="im-skip-actions">
            <button (click)="addSkipped()" class="im-btn im-btn--amber im-btn--sm">
              <i class="fa-solid fa-plus"></i> Add anyway
            </button>
          </div>
        </details>
      }
    </div>

    <div class="im-footer">
      <button (click)="resetToIdle()" class="im-btn im-btn--ghost">
        Import Another File
      </button>
      <button (click)="closeAndRefresh()" class="im-btn im-btn--primary">
        Done
      </button>
    </div>
  }

</div>
